import { useCallback, useMemo } from "react";

import { useRolesData } from "./useAccessData";

/**
 * "Used by N roles", plus the two flags that keep that number honest. The single derivation for both policy
 * surfaces — Settings → Políticas and `/app/access/policies` — so the two cannot answer differently.
 *
 * **Why it is derived at all.** `GET /internal/policies` returns the entity with no inverse relation, so the
 * only way to know whether a role links a policy is to cross the policies catalog with the roles one (each
 * role carries its `policies[]`).
 *
 * **Why the flags are not optional.** `roles` is `[]` in three different situations that the data cannot
 * distinguish: still loading, `roles:read` denied, and the request failed. In all three every policy comes
 * out as `usedBy: 0` — the exact shape of "no role links this", which is the signal an operator acts on when
 * deciding a policy is safe to remove, and which leads to the opposite action. `DELETE
 * /internal/policies/:id` answers **409 while any role links it**, so this number is also what decides
 * whether deleting is possible at all.
 *
 * **What the number means when it IS known.** It is exact for INTERNAL staff, whose roles catalog spans every
 * organization, and for an organization member looking at a policy of their OWN organization — a role from
 * another tenant cannot link it, because policy visibility on create is own-org plus global. It is a lower
 * bound only for a member looking at a GLOBAL policy, where a role in another organization may link it
 * unseen; that case never reaches the 409, because such a policy is already refused to a member on the
 * ownership check.
 */
export function usePolicyUsage() {
  const { roles, isLoading, hasNoAccess, hasError, load: reloadUsage } = useRolesData();

  const usageByPolicy = useMemo(() => {
    const map: Record<string, number> = {};
    for (const role of roles) {
      for (const policy of role.policies) {
        map[policy.id] = (map[policy.id] ?? 0) + 1;
      }
    }
    return map;
  }, [roles]);

  const usedByCount = useCallback((policyId: string) => usageByPolicy[policyId] ?? 0, [usageByPolicy]);

  return {
    usedByCount,
    /** The catalog is still arriving. Hold the figure back rather than render a zero it will contradict. */
    usageLoading: isLoading,
    /**
     * The catalog could not be read — denied (403) **or** failed (5xx, network). Both are covered because
     * both produce the same empty array, and the failure is by far the likelier of the two.
     */
    usageUnknown: hasNoAccess || hasError,
    reloadUsage,
  };
}
